import { BaseService } from "./BaseService";
import {
  SyncWhatsAppSystemFlowsRequest,
  SyncWhatsAppSystemFlowsResponse,
  WhatsAppBotReadinessStatusResponse,
  WhatsAppFlowOperationsStatus,
} from "@/types/communication-integration";
import {
  UpdateWhatsAppHotelConfigRequest,
  WhatsAppHotelConfig,
  WhatsAppLifecycleEvent,
  WhatsAppTemplate,
  WhatsAppTemplateBootstrapResult,
  WhatsAppTemplateBinding,
  WhatsAppTemplateBlueprint,
} from "@/types/whatsapp";

class WhatsAppProvisioningService extends BaseService {
  private basePath(hotelId: string): string {
    return `/hotels/${hotelId}/whatsapp/provisioning`;
  }

  private whatsappBasePath(hotelId: string): string {
    return `/hotels/${hotelId}/whatsapp`;
  }

  getFlowOperationsStatus(
    hotelId: string,
  ): Promise<WhatsAppFlowOperationsStatus> {
    return this.get<WhatsAppFlowOperationsStatus>(
      `${this.basePath(hotelId)}/flows/status`,
    );
  }

  getBotReadinessStatus(
    hotelId: string,
  ): Promise<WhatsAppBotReadinessStatusResponse> {
    return this.get<WhatsAppBotReadinessStatusResponse>(
      `${this.basePath(hotelId)}/bot/readiness`,
    );
  }

  getHotelConfig(hotelId: string): Promise<WhatsAppHotelConfig> {
    return this.get<WhatsAppHotelConfig>(
      `${this.whatsappBasePath(hotelId)}/config`,
      undefined,
      { disableToast: true },
    );
  }

  updateHotelConfig(
    hotelId: string,
    payload: UpdateWhatsAppHotelConfigRequest,
  ): Promise<WhatsAppHotelConfig> {
    return this.put<WhatsAppHotelConfig>(
      `${this.whatsappBasePath(hotelId)}/config`,
      payload,
    );
  }

  syncSystemFlows(
    hotelId: string,
    payload: SyncWhatsAppSystemFlowsRequest,
  ): Promise<SyncWhatsAppSystemFlowsResponse> {
    return this.post<SyncWhatsAppSystemFlowsResponse>(
      `${this.basePath(hotelId)}/flows/sync`,
      payload,
    );
  }

  listLifecycleTemplateBlueprints(
    hotelId: string,
    language = "en_US",
  ): Promise<WhatsAppTemplateBlueprint[]> {
    return this.get<WhatsAppTemplateBlueprint[]>(
      `${this.whatsappBasePath(hotelId)}/templates/blueprints`,
      { language },
    );
  }

  listTemplates(hotelId: string): Promise<WhatsAppTemplate[]> {
    return this.get<WhatsAppTemplate[]>(
      `${this.whatsappBasePath(hotelId)}/templates`,
    );
  }

  bootstrapLifecycleTemplates(
    hotelId: string,
  ): Promise<WhatsAppTemplateBootstrapResult> {
    return this.post<WhatsAppTemplateBootstrapResult>(
      `${this.whatsappBasePath(hotelId)}/templates/bootstrap`,
      {},
    );
  }

  createTemplateDraftFromBlueprint(
    hotelId: string,
    event: WhatsAppLifecycleEvent,
  ): Promise<WhatsAppTemplate> {
    return this.post<WhatsAppTemplate>(
      `${this.whatsappBasePath(hotelId)}/templates/blueprints/${event}/draft`,
      {},
    );
  }

  submitTemplateDraft(
    hotelId: string,
    templateId: string,
  ): Promise<WhatsAppTemplate> {
    return this.post<WhatsAppTemplate>(
      `${this.whatsappBasePath(hotelId)}/templates/${templateId}/submit`,
      {},
    );
  }

  listTemplateBindings(hotelId: string): Promise<WhatsAppTemplateBinding[]> {
    return this.get<WhatsAppTemplateBinding[]>(
      `${this.whatsappBasePath(hotelId)}/template-bindings`,
    );
  }

  bindTemplateFromRecommendedMetadata(
    hotelId: string,
    templateId: string,
  ): Promise<WhatsAppTemplateBinding> {
    return this.post<WhatsAppTemplateBinding>(
      `${this.whatsappBasePath(hotelId)}/template-bindings/from-template/${templateId}`,
      {},
    );
  }
}

export const whatsappProvisioningService = new WhatsAppProvisioningService();
